<template>
  <section class="settings-card">
    <div class="card-header">
      <!-- 文档 49：Ops 视角文案改为"我负责代理的神客信息" -->
      <h2 class="card-title">{{ isOpsRole ? '我负责代理的神客信息' : '神客信息总览' }}</h2>
      <div style="display:flex;align-items:center;gap:12px;">
        <el-input
          v-model="keyword"
          placeholder="搜索姓名/手机号"
          size="small"
          clearable
          style="width:200px;"
          prefix-icon="el-icon-search"
          @clear="loadData"
          @keyup.enter.native="loadData"
        />
        <el-select
          v-model="examStatusFilter"
          size="small"
          clearable
          placeholder="考试状态"
          style="width:140px;">
          <el-option label="待考试" :value="0" />
          <el-option label="已通过" :value="1" />
          <el-option label="未通过" :value="2" />
        </el-select>
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
        <el-button
          type="success"
          size="small"
          icon="el-icon-document"
          :loading="exporting"
          @click="handleExport">
          导出考试结果
        </el-button>
      </div>
    </div>
    <p class="card-desc" style="margin-bottom:16px;">
      {{ isOpsRole
        ? '仅展示归属于当前账号的代理添加的神客信息及考试认证状态。'
        : '展示本项目下所有代理添加的神客信息及考试认证状态。' }}
      共 <strong>{{ total }}</strong> 位神客<span v-if="examStatusFilter !== ''">，当前筛选 <strong>{{ filteredList.length }}</strong> 位</span>。
    </p>

    <div class="member-table-wrap" v-loading="loading">
      <table class="member-table" v-if="filteredList.length > 0">
        <thead>
          <tr>
            <th>神客姓名</th>
            <th>手机号</th>
            <th>身份证号</th>
            <th>所属代理</th>
            <th>考试状态</th>
            <th>考试成绩</th>
            <th>考试时间</th>
            <th>添加时间</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in pagedList" :key="row.id">
            <td>{{ row.shopperName }}</td>
            <td>{{ row.shopperPhone }}</td>
            <td class="td-muted">{{ maskIdCard(row.shopperIdCard) }}</td>
            <td>{{ row.agentName || '—' }}</td>
            <td>
              <span :class="['exam-tag', examTagClass(row.examStatus)]">{{ examStatusText(row.examStatus) }}</span>
            </td>
            <td>{{ row.examScore != null ? row.examScore + ' 分' : '—' }}</td>
            <td class="td-muted">{{ formatTime(row.examTime) || '—' }}</td>
            <td class="td-muted">{{ formatTime(row.createdAt) || '—' }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!loading" style="text-align:center;padding:40px 0;color:#94a3b8;">
        <i class="el-icon-user" style="font-size:32px;margin-bottom:8px;display:block;"></i>
        {{ list.length === 0 ? '暂无神客数据' : '当前筛选条件下无匹配数据' }}
      </div>
    </div>
    <div class="pagination" v-if="filteredList.length > pageSize">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="filteredList.length"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="p => currentPage = p" />
    </div>
  </section>
</template>

<script>
import { getProjectShoppers } from 'index/api/project/manager'

export default {
  name: 'ShopperOverviewSection',
  props: {
    projectId: { type: [String, Number], required: true }
  },
  data() {
    return {
      loading: false,
      exporting: false,
      keyword: '',
      examStatusFilter: '',
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      // 文档 51 P2-01：当前用户在该项目下的可见性范围（后端返回）
      myScope: null
    }
  },
  computed: {
    filteredList() {
      if (this.examStatusFilter === '' || this.examStatusFilter == null) return this.list
      const target = Number(this.examStatusFilter)
      return this.list.filter(r => Number(r.examStatus || 0) === target)
    },
    pagedList() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredList.slice(start, start + this.pageSize)
    },
    // 文档 53 P2-01：myScope 未加载时返回 null，模板用 === true/false 严格判断，
    //   避免依赖系统角色兜底造成短暂错位
    isOpsRole() {
      if (this.myScope && typeof this.myScope.isOps === 'boolean') return this.myScope.isOps
      return null
    },
    scopeLoaded() { return !!this.myScope }
  },
  watch: {
    keyword() { this.currentPage = 1 },
    examStatusFilter() { this.currentPage = 1 },
    projectId: {
      immediate: true,
      handler(v) { if (v) this.loadMyProjectScope() }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadMyProjectScope() {
      try {
        const res = await this.axios.get(`/ProjectManager/project/${this.projectId}/myScope`)
        const data = res?.data?.data || res?.data || res || {}
        this.myScope = data && (data.role !== undefined) ? data : null
      } catch { /* 失败时保持 myScope=null，不用全局角色兜底 */ }
    },
    async loadData() {
      this.loading = true
      try {
        const res = await getProjectShoppers(this.projectId, this.keyword || undefined)
        const data = res?.data?.data || res?.data || res || {}
        this.list = Array.isArray(data.list) ? data.list : []
        this.total = data.total || this.list.length
        this.currentPage = 1
      } catch (e) {
        console.error('加载神客总览失败', e)
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    examStatusText(status) {
      const map = { 0: '待考试', 1: '已通过', 2: '未通过' }
      return map[status] || '待考试'
    },
    examTagClass(status) {
      const map = { 0: 'pending', 1: 'passed', 2: 'failed' }
      return map[status] || 'pending'
    },
    maskIdCard(id) {
      if (!id || id.length < 8) return id || '—'
      return id.substring(0, 4) + '****' + id.substring(id.length - 4)
    },
    async handleExport() {
      this.exporting = true
      try {
        const token = localStorage.getItem('ACCESS_TOKEN')
        const baseURL = process.env.VUE_APP_BASE_URL || ''
        const res = await fetch(`${baseURL}/AgentShopper/exam/export-result-all?projectId=${encodeURIComponent(this.projectId)}&project_system_tag=jkd`, {
          headers: { 'Authorization': 'Bearer ' + token }
        })
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(text || `导出失败(HTTP ${res.status})`)
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const now = new Date()
        const pad = n => String(n).padStart(2, '0')
        const ts = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
        a.download = `考试结果_${ts}.zip`
        a.click()
        URL.revokeObjectURL(url)
        this.$message.success('考试结果已导出')
      } catch (e) {
        this.$message.error(e?.message || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      if (isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.exam-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;

  &.pending { background: #f0f0f0; color: #94a3b8; }
  &.passed  { background: #ecfdf5; color: #10b981; }
  &.failed  { background: #fef2f2; color: #ef4444; }
}

.pagination { margin-top: 16px; text-align: right; }
</style>
